import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
} from "@mui/material";
import axios from "axios";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";
import figLogo from "../../assets/fig_CROP.png";

const API_ENDPOINT = "https://figazuresql.swancreate.com/api/swa/FINANCE_expenses";

// Simple color palette
const textColor = "#000";
const backgroundColor = "#fff";
const headerBackground = "#f5f5f5";
const vendorHeaderBackground = "#e0e0e0"; // Slightly darker grey for vendor headers
const alternateRowEven = "#fff";
const alternateRowOdd = "#f9f9f9";

const MonthlyExpenseByVendorReport = () => {
    const [selectedMonth, setSelectedMonth] = useState("All");
    const [expenseData, setExpenseData] = useState([]);

    // Reference for capturing the entire report as PDF
    const reportRef = useRef(null);

    useEffect(() => {
        fetchExpenses();
    }, [selectedMonth]);

    const fetchExpenses = async () => {
        try {
            const response = await axios.get(API_ENDPOINT);
            const expenses = response.data
                .map((expense) => ({
                    ...expense,
                    TotalAmount: parseFloat(expense.Amount) || 0,
                    ExpenseDate: expense.ExpenseDate ? new Date(expense.ExpenseDate) : null,
                }))
                .filter(
                    (expense) =>
                        selectedMonth === "All" ||
                        (expense.ExpenseDate &&
                            expense.ExpenseDate.getMonth() + 1 === parseInt(selectedMonth))
                );
            setExpenseData(expenses);
        } catch (error) {
            console.error("Error fetching expenses:", error);
        }
    };

    const formatCurrency = (value) =>
        new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 2,
        }).format(value);

    // Group expenses by vendor name.
    const vendorGroups = expenseData.reduce((acc, expense) => {
        const vendor = expense.VendorName || "Uncategorized";
        if (!acc[vendor]) {
            acc[vendor] = { total: 0, items: [] };
        }
        acc[vendor].total += expense.TotalAmount;
        acc[vendor].items.push(expense);
        return acc;
    }, {});

    // Sort vendor names alphabetically (optional)
    const sortedVendors = Object.keys(vendorGroups).sort();

    /**
     * Download as PDF using html2canvas and jsPDF.
     */
    const handleDownloadPDF = async () => {
        if (!reportRef.current) return;
        try {
            const canvas = await html2canvas(reportRef.current, { scale: 2 });
            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF("p", "pt", "letter");
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const imageProps = pdf.getImageProperties(imgData);
            const pdfHeight = (imageProps.height * pdfWidth) / imageProps.width;
            pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
            pdf.save("MonthlyExpenseByVendorReport.pdf");
        } catch (error) {
            console.error("PDF generation error:", error);
        }
    };

    /**
     * Download as Excel using XLSX.
     */
    const handleDownloadExcel = () => {
        const workbook = XLSX.utils.book_new();
        const sheetData = expenseData.map((exp) => ({
            Date: exp.ExpenseDate?.toLocaleDateString() || "",
            Vendor: exp.VendorName || "",
            Category: exp.ExpenseCategory || "",
            Subcategory: exp.ExpenseSubcategory || "",
            Description: exp.Description || "",
            Amount: exp.TotalAmount,
        }));
        const sheet = XLSX.utils.json_to_sheet(sheetData);
        XLSX.utils.book_append_sheet(workbook, sheet, "Expenses");
        XLSX.writeFile(workbook, "MonthlyExpenseByVendorReport.xlsx");
    };

    let rowCounter = 0; // Global row counter for alternating row colors

    return (
        <Box
            sx={{
                backgroundColor,
                color: textColor,
                padding: "40px",
                width: "8.5in",
                minHeight: "11in",
                margin: "1in auto",
                borderRadius: "10px",
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                fontFamily: "Arial, sans-serif",
                lineHeight: 1.5,
            }}
        >
            {/* Download Buttons */}
            <Box sx={{ display: "flex", gap: "10px", justifyContent: "center", mb: 2 }}>
                <Button variant="contained" onClick={handleDownloadPDF}>
                    Download as PDF
                </Button>
                <Button variant="contained" onClick={handleDownloadExcel}>
                    Download as Excel
                </Button>
            </Box>

            {/* Report Container for PDF capture */}
            <Box ref={reportRef} sx={{ padding: "20px" }}>
                {/* Logo */}
                <Box
                    component="img"
                    src={figLogo}
                    alt="FIG K12 Logo"
                    sx={{ display: "block", margin: "0 auto 20px", maxWidth: "150px" }}
                />

                {/* Header */}
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: "bold",
                        textAlign: "center",
                        mb: "20px",
                        color: textColor,
                    }}
                >
                    Monthly Expense by Vendor Report
                </Typography>

                {/* Month Selector */}
                <Box display="flex" justifyContent="center" mb="20px">
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel sx={{ color: textColor }}>Month</InputLabel>
                        <Select
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                            sx={{ backgroundColor, color: textColor }}
                        >
                            <MenuItem value="All">All</MenuItem>
                            {Array.from({ length: 12 }, (_, i) => (
                                <MenuItem key={i + 1} value={i + 1}>
                                    {new Date(0, i).toLocaleString("default", { month: "long" })}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Main Table */}
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        backgroundColor: headerBackground,
                                        fontWeight: "bold",
                                        color: textColor,
                                    }}
                                >
                                    Expense Date
                                </TableCell>
                                <TableCell
                                    sx={{
                                        backgroundColor: headerBackground,
                                        fontWeight: "bold",
                                        color: textColor,
                                    }}
                                >
                                    Subcategory
                                </TableCell>
                                <TableCell
                                    sx={{
                                        backgroundColor: headerBackground,
                                        fontWeight: "bold",
                                        color: textColor,
                                    }}
                                >
                                    Description
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        backgroundColor: headerBackground,
                                        fontWeight: "bold",
                                        color: textColor,
                                    }}
                                >
                                    Amount
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedVendors.map((vendorName) => {
                                const { total, items } = vendorGroups[vendorName];
                                // Vendor header row (all using vendorHeaderBackground)
                                const vendorHeader = (
                                    <TableRow key={`${vendorName}-header`} sx={{ backgroundColor: vendorHeaderBackground }}>
                                        <TableCell colSpan={4} sx={{ fontWeight: "bold", color: textColor }}>
                                            Vendor: {vendorName} - Total: {formatCurrency(total)}
                                        </TableCell>
                                    </TableRow>
                                );

                                // Map each expense row for this vendor
                                const expenseRows = items.map((exp) => {
                                    rowCounter += 1;
                                    const rowBgColor = rowCounter % 2 === 0 ? alternateRowEven : alternateRowOdd;
                                    return (
                                        <TableRow key={exp.ExpenseID} sx={{ backgroundColor: rowBgColor }}>
                                            <TableCell sx={{ color: textColor }}>
                                                {exp.ExpenseDate ? exp.ExpenseDate.toLocaleDateString() : "N/A"}
                                            </TableCell>
                                            <TableCell sx={{ color: textColor }}>
                                                {exp.ExpenseSubcategory || "N/A"}
                                            </TableCell>
                                            <TableCell sx={{ color: textColor }}>
                                                {exp.Description || "N/A"}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: textColor }}>
                                                {formatCurrency(exp.TotalAmount)}
                                            </TableCell>
                                        </TableRow>
                                    );
                                });

                                return (
                                    <React.Fragment key={vendorName}>
                                        {vendorHeader}
                                        {expenseRows}
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
};

export default MonthlyExpenseByVendorReport;
